/**
 * RC8 Phase 1 · Slice 6 · Offline Foundation · Sync (Read-Through Cache).
 *
 * ONE composer (backend), ONE offline representation. This module NEVER
 * recomposes events or re-derives `time_context`. It:
 *   1. Fetches the canonical composed projection from the backend.
 *   2. Persists it in the appropriate class store (C2).
 *   3. Returns it to the caller with a `{ source, stale, fetched_at }`
 *      envelope.
 *
 * The offline path is a strict mirror: never mutates, never re-orders,
 * never re-computes. The backend is authoritative even when we're offline.
 */
import { C2TravelStore, OFFLINE_STATUS } from "./store";

const API = process.env.REACT_APP_BACKEND_URL;

// Sub-keys used inside C2 for the two canonical itinerary reads.
const C2_KEY_TODAY   = "today";
const C2_KEY_JOURNEY = "journey";


// In-memory cache of the authenticated user's id, resolved once via
// `/api/auth/me`. Persisting the user_id in localStorage would open a
// stale-tenant window on device handoff; keeping it in-memory means
// every browser tab / launch re-resolves against the live session.
let _cachedUserId = null;

async function _resolveUserId() {
  if (_cachedUserId) return _cachedUserId;
  try {
    const r = await fetch(`${API}/api/auth/me`, { credentials: "include" });
    if (!r.ok) return null;
    const me = await r.json();
    if (me && me.id) {
      _cachedUserId = me.id;
      return me.id;
    }
  } catch { /* offline · no id · caller decides */ }
  return null;
}

/**
 * Reset the in-memory user cache. Called on logout so a subsequent
 * caller resolves against the new session (or the unauthenticated state).
 */
export function resetOfflineIdentity() {
  _cachedUserId = null;
}


// LAW-005 · the client owns the voyage selection. Raw fetch bypasses the
// axios interceptor that injects `voyage_id`, so append it here explicitly.
function _withVoyage(path, voyage_id) {
  return voyage_id ? `${path}?voyage_id=${encodeURIComponent(voyage_id)}` : path;
}

async function _fetch(path) {
  const r = await fetch(`${API}${path}`, { credentials: "include" });
  if (!r.ok) {
    const err = new Error(`fetch ${path} · ${r.status}`);
    err.status = r.status;
    throw err;
  }
  return r.json();
}


/**
 * Read-through wrapper for `/api/cruises/current/today`.
 *
 * Behaviour:
 *   · online + first launch  → fetch → persist → return { source: "network" }
 *   · online + already cached → fetch → persist → return { source: "network" }
 *                                (refresh · read-through)
 *   · offline + cache hit    → return { source: "cache" }
 *   · offline + no cache     → return { source: "empty", error: "offline_no_cache" }
 *
 * @param {{user_id: string, voyage_id: string}} scope
 */
export async function syncToday({ user_id, voyage_id }) {
  const meta = {
    source: "network",
    stale: false,
    fetched_at: null,
    offline_status: OFFLINE_STATUS,
  };
  const uid = user_id || await _resolveUserId();
  try {
    const data = await _fetch(_withVoyage("/api/cruises/current/today", voyage_id));
    // Persist under (user, voyage). If the response referenced a
    // different voyage than the scope we were given, honour the
    // server-authoritative voyage id.
    const canonicalVoyageId =
      (data && data.voyage && data.voyage.id) || voyage_id;
    if (uid && canonicalVoyageId) {
      // Belt & braces: purge any C2 rows written under other user_ids
      // on this device (e.g. after a login handoff).
      await C2TravelStore.purgeForeign(uid);
      await C2TravelStore.put(
        uid, canonicalVoyageId, C2_KEY_TODAY, data,
      );
    }
    meta.fetched_at = Date.now();
    return { data, meta };
  } catch (e) {
    // Attempt cache fallback — offline read reliability.
    if (uid && voyage_id) {
      const cached = await C2TravelStore.get(uid, voyage_id, C2_KEY_TODAY);
      if (cached) {
        return {
          data: cached,
          meta: { ...meta, source: "cache", stale: true, error: String(e) },
        };
      }
    }
    return {
      data: null,
      meta: { ...meta, source: "empty", stale: false, error: String(e) },
    };
  }
}


/**
 * Read-through wrapper for `/api/cruises/current/journey`.
 * Semantics identical to `syncToday`.
 */
export async function syncJourney({ user_id, voyage_id }) {
  const meta = {
    source: "network",
    stale: false,
    fetched_at: null,
    offline_status: OFFLINE_STATUS,
  };
  const uid = user_id || await _resolveUserId();
  try {
    const data = await _fetch(_withVoyage("/api/cruises/current/journey", voyage_id));
    const canonicalVoyageId =
      (data && data.voyage && data.voyage.id) || voyage_id;
    if (uid && canonicalVoyageId) {
      await C2TravelStore.purgeForeign(uid);
      await C2TravelStore.put(
        uid, canonicalVoyageId, C2_KEY_JOURNEY, data,
      );
    }
    meta.fetched_at = Date.now();
    return { data, meta };
  } catch (e) {
    if (uid && voyage_id) {
      const cached = await C2TravelStore.get(uid, voyage_id, C2_KEY_JOURNEY);
      if (cached) {
        return {
          data: cached,
          meta: { ...meta, source: "cache", stale: true, error: String(e) },
        };
      }
    }
    return {
      data: null,
      meta: { ...meta, source: "empty", stale: false, error: String(e) },
    };
  }
}


/**
 * Convenience: read the cached today/journey without touching the
 * network. Used for first-paint before the fetch completes.
 */
export async function readCachedToday({ user_id, voyage_id }) {
  const uid = user_id || await _resolveUserId();
  if (!uid || !voyage_id) return null;
  return C2TravelStore.get(uid, voyage_id, C2_KEY_TODAY);
}

export async function readCachedJourney({ user_id, voyage_id }) {
  const uid = user_id || await _resolveUserId();
  if (!uid || !voyage_id) return null;
  return C2TravelStore.get(uid, voyage_id, C2_KEY_JOURNEY);
}
